import './Editor.style.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FileManager } from 'utils/FileManager';
import { STORE_URL } from 'utils';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Quill 모듈 라이브러리
import QuillImageDropAndPaste from 'quill-image-drop-and-paste'; // npm i quill-image-drop-and-paste
import ImageResize from 'quill-image-resize-module-react'; // npm i quill-image-resize-module-react
import MagicUrl from 'quill-magic-url'; // npm i quill-magic-url

// Quill 모듈 등록
Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste);
Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/magicUrl', MagicUrl);
window.Quill = Quill;

// 사용자 지정 폰트 등록
const Font = Quill.import('formats/font');
Font.whitelist = ['Ubuntu', 'Raleway', 'Roboto'];
Quill.register(Font, true);

// 사용자 지정 폰트 사이즈
const Size = Quill.import('attributors/style/size');
Size.whitelist = [
  '7px',
  '8px',
  '9px',
  '10px',
  '11px',
  '12px',
  '13px',
  '14px',
  '18px',
  '24px',
  '36px',
];
Quill.register(Size, true);

// 커스텀 iframe 모듈: iframe을 감싸는 div 추가
const BlockEmbed = Quill.import('blots/block/embed');
class VideoBlot extends BlockEmbed {
  static blotName = 'video';
  static tagName = 'div';
  static className = 'video-container';

  static create(value) {
    const node = super.create();

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', value);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', true);
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );

    node.appendChild(iframe);

    return node;
  }

  static value(node) {
    const iframe = node.querySelector('iframe');
    return iframe ? iframe.getAttribute('src') : '';
  }
}
Quill.register(VideoBlot);

// 커스텀 이미지 모듈: 이미지 업로드 시, width=100%
const Image = Quill.import('formats/image');
class CustomImage extends Image {
  static create(value) {
    let node = super.create(value);
    node.setAttribute('width', '100%');
    return node;
  }

  static formats(domNode) {
    return {
      width: domNode.getAttribute('width') || '100%',
    };
  }

  format(name, value) {
    if (name === 'width') {
      this.domNode.setAttribute('width', value);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(CustomImage, true);

// 커스텀 리사이즈 모듈 BaseModule
class BaseModule {
  constructor(resizer) {
    this.overlay = resizer.overlay;
    this.img = resizer.img;
    this.options = resizer.options;
    this.requestUpdate = resizer.onUpdate;
  }
  onCreate = () => {};
  onDestroy = () => {};
  onUpdate = () => {};
}

// 커스텀 리사이즈 모듈: pixel -> 비율(%)
class CustomResize extends BaseModule {
  onCreate = () => {
    this.boxes = [];

    this.addBox('nwse-resize');
    this.addBox('nesw-resize');
    this.addBox('nwse-resize');
    this.addBox('nesw-resize');

    this.positionBoxes();
  };

  onDestroy = () => {
    this.setCursor('');
  };

  positionBoxes = () => {
    const handleXOffset = `${
      -parseFloat(this.options.handleStyles.width) / 2
    }px`;
    const handleYOffset = `${
      -parseFloat(this.options.handleStyles.height) / 2
    }px`;

    [
      { left: handleXOffset, top: handleYOffset },
      { right: handleXOffset, top: handleYOffset },
      { right: handleXOffset, bottom: handleYOffset },
      { left: handleXOffset, bottom: handleYOffset },
    ].forEach((pos, idx) => {
      Object.assign(this.boxes[idx].style, pos);
    });
  };

  addBox = (cursor) => {
    const box = document.createElement('div');

    Object.assign(box.style, this.options.handleStyles);
    box.style.cursor = cursor;

    box.style.width = `${this.options.handleStyles.width}px`;
    box.style.height = `${this.options.handleStyles.height}px`;

    box.addEventListener('mousedown', this.handleMousedown, false);
    this.overlay.appendChild(box);
    this.boxes.push(box);
  };

  handleMousedown = (evt) => {
    this.dragBox = evt.target;
    this.dragStartX = evt.clientX;
    this.preDragWidth = this.img.width || this.img.naturalWidth;
    this.setCursor(this.dragBox.style.cursor);
    document.addEventListener('mousemove', this.handleDrag, false);
    document.addEventListener('mouseup', this.handleMouseup, false);
  };

  handleMouseup = () => {
    this.setCursor('');
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.handleMouseup);
  };

  // 여기서 제어
  handleDrag = (evt) => {
    if (!this.img) {
      return;
    }
    const parentWidth = this.img.parentElement.offsetWidth;
    const deltaX = evt.clientX - this.dragStartX;
    let newWidth;
    if (this.dragBox === this.boxes[0] || this.dragBox === this.boxes[3]) {
      newWidth = Math.round(this.preDragWidth - deltaX);
    } else {
      newWidth = Math.round(this.preDragWidth + deltaX);
    }

    let newWidthPercent = (newWidth / parentWidth) * 100;
    if (newWidthPercent > 100) {
      newWidthPercent = 100;
    }
    this.img.removeAttribute('style');
    this.img.setAttribute('width', `${newWidthPercent}%`);
    this.requestUpdate();
  };

  setCursor = (value) => {
    [document.body, this.img].forEach((el) => {
      el.style.cursor = value; // eslint-disable-line no-param-reassign
    });
  };
}

// 툴바 옵션 설정
const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ background: [] }, { color: [] }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  [{ list: 'bullet' }, { list: 'ordered' }],
  ['blockquote', 'code-block'],
  ['image', 'video'],
  [{ size: Size.whitelist }],
  [{ font: Font.whitelist }],
];

// 툴바 포맷 설정
const formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'align',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'background',
  'color',
  'link',
  'image',
  'video',
  'width',
  'code-block',
];

const Editor = ({
  initialContent = '', // '수정'시, 기존 게시글 내용

  // Quill 에디터
  placeholder = '내용을 입력하세요.',

  width = '100%',
  height,

  editorOnChange, // 에디터 내용 - editor onChange
  readOnly = false,

  toggleHtml = true, // html 토글 표시 여부

  path = 'default', // 이미지 업로드 경로

  maxImageSize = 5 * 1024 * 1024, // 이미지 파일 크기
  maxImageCount = 2, // 이미지 갯수
}) => {
  /*===== State ===== */

  const [value, setValue] = useState(initialContent);
  const [isHtml, setIsHtml] = useState(false);
  const quillRef = useRef(null);

  const MAX_IMAGE_SIZE = maxImageSize;
  const MAX_IMAGE_COUNT = maxImageCount;

  /* ===== Function ===== */

  // 이미지 업로드 핸들러
  const handleImage = async (file) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    if (!range) return alert('에디터가 포커스되지 않았습니다.');

    const delta = editor.getContents();
    const ops = delta.ops;
    const filteredOps = ops.filter((op) => {
      return op.insert && op.insert.image;
    });

    // if (filteredOps.length >= MAX_IMAGE_COUNT) {
    //   return alert('이미지 갯수 제한');
    // }

    if (file.size > MAX_IMAGE_SIZE) {
      return alert('이미지 용량 제한');
    }

    try {
      const result = await FileManager.upload(path, file);
      const url = `${STORE_URL}/${result.data.file_name}`;
      editor.insertEmbed(range.index, 'image', url);
      editor.setSelection(range.index + 1);
    } catch (error) {
      console.error(error);
    }
  };

  // 드롭 이벤트 핸들러
  const handleDrop = async (e) => {
    e.preventDefault();

    const file = e.dataTransfer.files[0];

    if (file && file.type.startsWith('image/')) {
      await handleImage(file);
      e.stopPropagation();
    }
  };

  // 붙여넣기 이벤트 핸들러
  const handlePaste = async (e) => {
    const pasteData = e.clipboardData || window.clipboardData;
    if (pasteData && pasteData.items) {
      let hasImage = false;
      for (const item of pasteData.items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            hasImage = true;

            await handleImage(file);
          }
        }
      }
      if (hasImage) {
        e.preventDefault();
      }
    }
  };

  // HTML 토글 핸들러
  const handleToggleHtml = () => {
    setIsHtml(!isHtml);
    if (!isHtml) {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        setValue(editor.root.innerHTML);
      }
    } else {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.clipboard.dangerouslyPasteHTML(value);
      }
    }
  };

  // HTML 변경 핸들러
  const handleHtmlChange = (e) => {
    const htmlValue = e.target.value;
    setValue(htmlValue);
    if (editorOnChange) editorOnChange(htmlValue);
  };

  /* ===== Hooks ===== */

  // Quill 모듈 설정: useMemo 필수 사용
  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbarOptions,
      },
      imageDropAndPaste: {
        // 이미지 드롭 및 붙여넣기 모듈
        handler: handlePaste,
      },
      imageResize: {
        // 이미지 리사이즈 모듈
        displaySize: true,
        resize: true,
        modules: [CustomResize],
      },
      clipboard: { matchVisual: false },
      magicUrl: true, // 주소 자동 링크 모듈
    }),
    // eslint-disable-next-line
    []
  );

  // 에디터 설정 Hook
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const toolbar = editor.getModule('toolbar');
      const quillContainer = editor.root;

      // Toolbar를 통한 이미지 업로드
      toolbar.addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = () => {
          const file = input.files[0];
          handleImage(file);
        };
      });

      quillContainer.addEventListener('drop', handleDrop);
      quillContainer.addEventListener('paste', handlePaste);

      return () => {
        quillContainer.removeEventListener('drop', handleDrop);
        quillContainer.removeEventListener('paste', handlePaste);
      };
    }
  });

  useEffect(() => {
    if (initialContent) {
      setValue(initialContent);
    } else {
      setValue('');
    }
  }, [initialContent]);
  // 배너 크기에 따른 Quill Container 높이 조절 Hook
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current?.getEditor();

      const toolbar = editor.getModule('toolbar');
      const toolbarHeight = toolbar.container.offsetHeight; // 툴바 높이

      const quillContainer = quillRef.current?.editor.container;
      quillContainer.style.height = `${height - toolbarHeight - 25}px`; // 25 = html 토글 버튼 높이
      quillContainer.style.width = `${width}px`;
    }
  }, [height, width, isHtml]);

  // 이미지 삽입 시, 'width=100%'으로 삽입 Hook
  useEffect(() => {
    const quill = quillRef.current.getEditor();

    quill.clipboard.addMatcher('IMG', (node, delta) => {
      const image = delta.ops[0];
      if (image && image.insert && image.insert.image) {
        return {
          ...delta,
          ops: [
            {
              insert: {
                image: image.insert.image,
                width: '100%',
              },
            },
          ],
        };
      }
      return delta;
    });
  }, []);

  /* ===== Render ===== */
  return (
    <div
      className="edit_container"
      style={{ width: width, height: `${height}px` }}
    >
      {toggleHtml ? (
        <div className="html_button_box">
          <button onClick={handleToggleHtml} disabled={!isHtml}>
            기본
          </button>
          <button onClick={handleToggleHtml} disabled={isHtml}>
            HTML
          </button>
        </div>
      ) : (
        <></>
      )}
      <div className="edit_wrapper" style={{ width: '100%' }}>
        {isHtml ? (
          <div style={{ width: width, height: `${height - 25}px` }}>
            <textarea
              style={{ width: '100%', height: '100%' }}
              value={value}
              onChange={handleHtmlChange}
              readOnly={readOnly}
            />
          </div>
        ) : (
          <ReactQuill
            ref={quillRef}
            placeholder={placeholder}
            value={value}
            modules={modules}
            formats={formats}
            onChange={(content, delta, source, editor) => {
              setValue(editor.getHTML());

              if (editorOnChange) editorOnChange(editor.getHTML());
            }}
            readOnly={readOnly}
            height={'auto'}
            theme="snow"
          />
        )}
      </div>
    </div>
  );
};

export default Editor;
